import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import './login.css';
import { WaveLogo, AmInput } from './shared';
import { useLoginMutation } from '../../store/services/api';
import useWidthBreakpoint, { Breakpoint } from '../../hooks/useWidthBreakpoint';
import DemoEntryButton from './DemoEntryButton';

/* ── Matrix rain background ──────────────────────────────────── */

const GLYPHS =
  '01010110ABCDEFXYZK$%+-*/<>{}[]#@?!&=:;.,01234567890101£¥€§¶†‡↑↓▲▼●○';

const MatrixRainColumn = ({ c, cols }: { c: number; cols: number }) => {
  const seed = ((c + 1) * 137) % 9999;
  if (seed % 6 === 0) return null;

  const dur = 16 + (seed % 22);
  const delay = -((seed * 11) % (dur * 1000)) / 1000;
  const left = (c / cols) * 100;
  const len = 20;
  const chars = Array.from({ length: len * 2 }).map(
    (_, k) => GLYPHS[(seed * 7 + k * 13) % GLYPHS.length],
  );
  const isRed = seed % 14 < 2;
  const trailColor = isRed ? '#F0162F' : '#00B061';
  const baseOpacity = 0.08 + (seed % 8) * 0.02;
  const fontSize = 11 + (seed % 6);

  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: `${left}%`,
        width: `${100 / cols}%`,
        height: '200%',
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
        fontSize,
        lineHeight: 1.5,
        textAlign: 'center',
        fontWeight: 600,
        animation: `amRainDownFade ${dur}s linear ${delay}s infinite`,
        willChange: 'transform',
      }}
    >
      {chars.map((ch, k) => {
        const headIdx = k % len;
        const distFromHead = headIdx;
        const isHead = distFromHead === 0;
        const fade = Math.min(1, distFromHead / (len - 4));
        return (
          <div
            key={k}
            style={{
              color: trailColor,
              opacity: isHead
                ? Math.min(1, baseOpacity * 4)
                : baseOpacity * (1 - Math.pow(fade, 1)) + 0.1,
              textShadow: isHead ? `0 0 4px ${trailColor}` : 'none',
            }}
          >
            {ch}
          </div>
        );
      })}
    </div>
  );
};

const MatrixRainBg = ({ cols = 38 }: { cols?: number }) => {
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
      }}
    >
      {Array.from({ length: cols }).map((_, c) => (
        <MatrixRainColumn key={c} c={c} cols={cols} />
      ))}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(to bottom, #000 0%, transparent 14%, transparent 86%, #000 100%)',
        }}
      />
    </div>
  );
};

/* ── Left form ───────────────────────────────────────────────── */

const LeftForm = ({
  breakpoint,
  handleOnboardingStart,
  handleOnboardingInProgress,
}: {
  breakpoint: Breakpoint;
  handleOnboardingStart: () => void;
  handleOnboardingInProgress: (userId: number, onboardingStep: number) => void;
}) => {
  const [login] = useLoginMutation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const [btnHovered, setBtnHovered] = useState(false);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!email || !password) return;

      setLoading(true);
      const result = await login({ email, password });

      if (result.data?.error) {
        setError(result.data.error);
      }

      if (result.data?.success && result.data?.onboardingStep) {
        handleOnboardingInProgress(
          result.data.userId,
          result.data.onboardingStep,
        );
      }

      setLoading(false);
    },
    [email, handleOnboardingInProgress, login, password],
  );

  const isMobile = breakpoint === 'sm';
  const isTablet = breakpoint === 'md';

  const formWidth = isMobile ? '100%' : isTablet ? 400 : 420;
  const formPadding = isMobile
    ? '40px 24px'
    : isTablet
    ? '36px 36px'
    : '36px 44px';

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        width: formWidth,
        maxWidth: isMobile ? 420 : undefined,
        margin: isMobile ? '0 auto' : undefined,
        padding: formPadding,
        display: 'flex',
        flexDirection: 'column',
        borderRight: isMobile ? 'none' : '2px double rgba(255,255,255,.5)',
        position: 'relative',
        flex: isMobile ? '1' : '0 0 auto',
        boxSizing: 'border-box',
        background: isMobile ? 'rgba(0,0,0,0.5)' : undefined,
        zIndex: isMobile ? 1 : undefined,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          marginBottom: 40,
        }}
      >
        <span
          style={{
            width: 11,
            height: 11,
            background: '#00B061',
            boxShadow: '0 0 10px rgba(0,176,97,.7)',
            borderRadius: 1,
            transform: 'rotate(45deg)',
            flexShrink: 0,
          }}
        />
        <WaveLogo size={20} />
      </div>

      <div
        style={{
          marginBottom: 4,
          color: '#808080',
          fontSize: 11,
          letterSpacing: '.22em',
          textTransform: 'uppercase',
        }}
      >
        / 01 — Sign in
      </div>
      <h1
        style={{
          fontSize: isTablet ? 36 : 42,
          fontWeight: 600,
          margin: '0 0 8px 0',
          lineHeight: 1,
          letterSpacing: '-.01em',
          fontFamily: 'Kanit, sans-serif',
        }}
      >
        Welcome back, <span style={{ color: '#00B061' }}>operator</span>.
      </h1>
      <p
        style={{
          color: '#808080',
          fontSize: isTablet ? 12 : 14,
          lineHeight: 1.5,
          margin: isTablet ? '0 0 32px 0' : '0 0 62px 0',
        }}
      >
        Manage your Subscriptions. Distribute your Balance. Discover new coins.
        Gloat over pretty charts and enjoy your profit.
      </p>

      <AmInput
        label="Email"
        type="email"
        value={email}
        onChange={setEmail}
        style={{ marginBottom: 18 }}
      />
      <AmInput
        label="Password"
        type="password"
        value={password}
        onChange={setPassword}
        style={{ marginBottom: 8 }}
      />

      {error && (
        <div
          style={{
            fontSize: 11,
            color: '#F0162F',
            marginBottom: 8,
            fontFamily:
              'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
            letterSpacing: '.04em',
          }}
        >
          ! {error}
        </div>
      )}

      <button
        type="submit"
        disabled={loading}
        onMouseEnter={() => !loading && setBtnHovered(true)}
        onMouseLeave={() => setBtnHovered(false)}
        style={{
          marginTop: 16,
          height: 52,
          background: loading ? '#1a1a1a' : btnHovered ? '#00C870' : '#00B061',
          color: loading ? '#808080' : '#000',
          fontFamily: 'Kanit, sans-serif',
          fontSize: 13,
          fontWeight: 700,
          letterSpacing: '.18em',
          textTransform: 'uppercase',
          border: 'none',
          cursor: loading ? 'default' : 'pointer',
          boxShadow:
            !loading && btnHovered ? '0 0 22px rgba(0,176,97,.55)' : 'none',
          transform: !loading && btnHovered ? 'translateY(-1px)' : 'none',
          transition: 'background .12s, box-shadow .12s, transform .1s',
        }}
      >
        {loading ? 'Signing in...' : 'Sign in to Dashboard ›'}
      </button>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          marginTop: 12,
          fontSize: 11,
          color: '#808080',
          letterSpacing: '.06em',
        }}
      >
        <span>No account?</span>
        <span
          style={{
            color: '#00B061',
            cursor: 'pointer',
            borderBottom: '1px solid rgba(0,176,97,.4)',
          }}
          onClick={handleOnboardingStart}
        >
          Register
        </span>
      </div>
    </form>
  );
};

/* ── Right panel content ─────────────────────────────────────── */

const RightContent = ({ breakpoint }: { breakpoint: Breakpoint }) => {
  const isTablet = breakpoint === 'md';

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        padding: isTablet ? '28px 28px' : '36px 40px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontFamily:
            'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
          fontSize: 10,
          color: '#808080',
          letterSpacing: '.22em',
        }}
      >
        <span style={{ textTransform: 'uppercase' }}>
          {'// 02 — runtime manifesto'}
        </span>
        <span>v9.1.4</span>
      </div>

      <div>
        <div
          style={{
            fontFamily: "'Bebas Neue', Kanit, sans-serif",
            fontSize: isTablet ? 68 : 86,
            lineHeight: 0.92,
            letterSpacing: '.02em',
            textTransform: 'uppercase',
          }}
        >
          Trade <span style={{ color: '#00B061' }}>signals,</span>
          <br />
          not <span style={{ color: '#F0162F' }}>noise.</span>
        </div>
        <div
          style={{
            marginTop: 26,
            paddingTop: 16,
            borderTop: '1px double rgba(255,255,255,.5)',
            display: 'flex',
            gap: 0,
            alignItems: 'stretch',
          }}
        >
          {(
            [
              [
                'Algorithmic',
                'Strategies authored in code, executed without emotions.',
              ],
              [
                'Multi-exchange',
                'One united system across Binance, KuCoin, MEXC and Coinbase.',
              ],
              [
                'All-Weather Returns',
                'Profits compounded when markets rise. Capital shielded when they fall. One system, two directions.',
              ],
            ] as [string, string][]
          ).map(([h, p], i) => (
            <div
              key={h}
              style={{
                flex: 1,
                paddingRight: isTablet ? 12 : 18,
                borderLeft: i === 0 ? 'none' : '1px solid #181818',
                paddingLeft: i === 0 ? 0 : isTablet ? 12 : 18,
              }}
            >
              <div
                style={{
                  fontFamily:
                    'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                  fontSize: 10,
                  color: '#00B061',
                  letterSpacing: '.22em',
                  textTransform: 'uppercase',
                  marginBottom: 4,
                }}
              >
                0{i + 1}
              </div>
              <h3
                style={{
                  margin: '0 0 3px 0',
                  fontSize: isTablet ? 14 : 16,
                  fontWeight: 600,
                }}
              >
                {h}
              </h3>
              <p
                style={{
                  margin: 0,
                  fontSize: isTablet ? 11 : 13,
                  color: '#808080',
                  lineHeight: 1.5,
                }}
              >
                {p}
              </p>
            </div>
          ))}
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          gap: 8,
          alignItems: 'center',
          fontFamily:
            'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
          fontSize: 10,
          color: '#7a7a7a',
          letterSpacing: '.18em',
          textTransform: 'uppercase',
        }}
      >
        {(['binance', 'kucoin', 'mexc', 'coinbase'] as const).map((name) => (
          <React.Fragment key={name}>
            <span
              style={{
                width: 7,
                height: 7,
                background: '#00B061',
                boxShadow: '0 0 8px rgba(0,176,97,.75)',
                borderRadius: 1,
                transform: 'rotate(45deg)',
                flexShrink: 0,
                display: 'inline-block',
                marginBottom: 2,
              }}
            />
            <span style={{ marginRight: 16 }}>{name}</span>
          </React.Fragment>
        ))}
        {/* Demo Account entry — extracted so this screen has no demo wiring. */}
        <DemoEntryButton />
      </div>
    </div>
  );
};

/* ── Main export ─────────────────────────────────────────────── */

const LoginScreen = ({
  handleOnboardingStart,
  handleOnboardingInProgress,
}: {
  handleOnboardingStart: () => void;
  handleOnboardingInProgress: (userId: number, onboardingStep: number) => void;
}) => {
  const { breakpoint } = useWidthBreakpoint();
  const isMobile = breakpoint === 'sm';

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        background: '#000',
        color: '#fff',
        fontFamily: 'Kanit, sans-serif',
        display: 'flex',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {isMobile && <MatrixRainBg cols={30} />}
      <LeftForm
        breakpoint={breakpoint}
        handleOnboardingStart={handleOnboardingStart}
        handleOnboardingInProgress={handleOnboardingInProgress}
      />
      {!isMobile && (
        <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
          <MatrixRainBg />
          <RightContent breakpoint={breakpoint} />
        </div>
      )}
    </div>
  );
};

export default LoginScreen;
